import React from "react";
import { CS_TEL_HREF, CS_PHONE_DISPLAY, AUTO_PHONE_DISPLAY } from "@/brand";
import { LegalLayout } from "@/pages/legal/LegalLayout";

// The SMS section on this page is written to satisfy CTIA/TCPA best-practice
// guidance and Twilio's A2P 10DLC brand & campaign review. Do not delete the
// "no mobile information sharing" paragraph — Twilio's registration reviewer
// looks for it verbatim.
const PrivacyPolicy = () => (
  <LegalLayout
    title="Privacy Policy"
    subtitle="How Enchanted Gear Rentals collects, uses, and safeguards the information you entrust to us."
    updated="June 14, 2026"
    seoDesc="Enchanted Gear Rentals privacy policy explaining what personal information we collect, how we use it, and how our SMS program handles consent and opt-outs."
  >
    <p>
      Enchanted Gear Rentals LLC ("Enchanted Gear Rentals," "we," "us," "our")
      operates <a href="https://enchantedgearrentals.com">enchantedgearrentals.com</a>{" "}
      and provides baby gear rentals to families visiting the Walt Disney World
      area. This Privacy Policy explains what personal information we collect,
      why we collect it, how we protect it, and the choices you have about your
      information. By using our website or booking a rental, you agree to the
      practices described below.
    </p>

    <h2>1. Information We Collect</h2>
    <h3>Information you provide</h3>
    <ul>
      <li><strong>Contact details</strong> — first and last name, email address, mobile phone number.</li>
      <li><strong>Booking details</strong> — hotel or resort name, room number (if known), delivery/pickup dates and time slots, gear selections, ages of children, special notes.</li>
      <li><strong>Payment details</strong> — billing name, billing address, and card details, all of which are entered directly into our payment processor (Stripe) and never stored on our servers. We only retain the last four digits of the card and the Stripe customer/payment method identifiers required to service refunds and off-session charges you have pre-authorized.</li>
      <li><strong>Support content</strong> — messages you send us via email, SMS, WhatsApp, phone, or the website contact forms.</li>
      <li><strong>Damage / return photos</strong> — images we (or you) upload during return inspection.</li>
    </ul>
    <h3>Information collected automatically</h3>
    <ul>
      <li><strong>Device &amp; usage data</strong> — IP address, browser type, referring URL, pages viewed, and timestamps.</li>
      <li><strong>Cookies</strong> — small text files used to keep you signed in, remember your cart, and measure aggregate site performance. You can disable non-essential cookies in your browser settings.</li>
    </ul>

    <h2>2. How We Use Your Information</h2>
    <ul>
      <li>To accept and fulfill your rental booking — including delivery, pickup, and driver coordination.</li>
      <li>To send transactional confirmations, delivery reminders, change-request updates, review requests, and receipts by email and, if you have opted in, by SMS or WhatsApp.</li>
      <li>To process payments, refunds, incidental charges (e.g. damage or extended-rental fees you have pre-authorized), and to prevent fraud.</li>
      <li>To provide customer support and respond to your questions.</li>
      <li>To improve our website, catalog, and delivery operations through aggregated analytics.</li>
      <li>To comply with legal obligations, enforce our Terms, and defend our rights.</li>
    </ul>

    <h2>3. SMS &amp; WhatsApp Messaging Program</h2>
    <div className="callout">
      <strong>SMS Consent &amp; Data Sharing</strong>
      No mobile information will be shared with third parties or affiliates for
      marketing or promotional purposes. All of the categories described in
      this Privacy Policy exclude text-messaging originator opt-in data and
      consent — that information will not be shared with any third parties.
    </div>
    <p>
      Enchanted Gear Rentals operates an application-to-person (A2P) SMS and
      WhatsApp messaging program delivered through Twilio, Inc. The program is
      strictly transactional and customer-service oriented.
    </p>
    <h3>How you opt in</h3>
    <ul>
      <li>By checking the optional "SMS Updates" box on our checkout page and providing your mobile number. The box is unchecked by default and is not required to complete your booking — we record the date, time, and source of your consent.</li>
      <li>By explicitly asking a team member via phone, email, or in-person to text you about your reservation.</li>
      <li>By replying "START" or "YES" to a previous message from us.</li>
    </ul>
    <h3>If you do not opt in</h3>
    <p>
      If you choose not to receive SMS updates, we will not text you. We may
      attempt to contact you by email instead for delivery and pickup
      coordination. You remain responsible for being present in your resort
      lobby for the entire selected delivery and pickup window as described in
      our <a href="/terms">Terms of Service</a>.
    </p>
    <h3>Driver communication privacy</h3>
    <p>
      Delivery drivers communicate with you exclusively through Enchanted Gear
      Rentals' secure messaging system. Texts are sent from our business
      messaging number and emails from our business email address. Drivers are
      never shown, and cannot access, your personal phone number or email
      address — only authorized administrators can view that information.
    </p>
    <h3>What we may text you</h3>
    <ul>
      <li>Booking confirmations and receipts.</li>
      <li>Delivery windows, driver "on the way" alerts, and pickup reminders.</li>
      <li>Change-request approvals, refund confirmations, and payment retries.</li>
      <li>Return-inspection results and post-trip review requests.</li>
      <li>Occasional service updates about your active reservation.</li>
    </ul>
    <p>
      Message frequency varies by booking activity — typically 3 to 8 messages
      per rental. Message and data rates may apply. Consent to receive text
      messages is not a condition of purchasing any goods or services from us.
    </p>
    <h3>How to opt out or get help</h3>
    <ul>
      <li>Reply <strong>STOP</strong> to any message to unsubscribe from all future SMS. You will receive a single confirmation message and no further texts.</li>
      <li>Reply <strong>HELP</strong> for support information, or contact us at{" "}
        <a href="mailto:info@enchantedgearrentals.com">info@enchantedgearrentals.com</a>{" "}
        or call/text <a href={CS_TEL_HREF}>{CS_PHONE_DISPLAY}</a>.</li>
      <li><strong>Sending number</strong> — automated, order-related text messages are sent from <strong>{AUTO_PHONE_DISPLAY}</strong>. For a human, call or text {CS_PHONE_DISPLAY}.</li>
      <li>Supported carriers include AT&amp;T, T-Mobile, Verizon Wireless, Sprint, Boost, Cricket, U.S. Cellular, MetroPCS, and most other U.S. carriers. Carriers are not liable for delayed or undelivered messages.</li>
    </ul>

    <h2>4. How We Share Your Information</h2>
    <p>We do not sell your personal information. We share it only with vendors and only to the extent necessary to run our business:</p>
    <ul>
      <li><strong>Stripe, Inc.</strong> — payment processing, saved cards, and payouts.</li>
      <li><strong>Twilio, Inc.</strong> — SMS and WhatsApp delivery. Opt-in data and consent records stay with Twilio for compliance purposes and are not shared for marketing.</li>
      <li><strong>Resend</strong> — transactional email delivery.</li>
      <li><strong>Google Maps</strong> — address validation and delivery-zone geocoding.</li>
      <li><strong>Emergent Object Storage</strong> — durable image storage for damage photos and product imagery.</li>
      <li><strong>Legal &amp; safety</strong> — if compelled by valid subpoena, court order, or to protect the rights, safety, or property of Enchanted Gear Rentals, our customers, or the public.</li>
    </ul>
    <p>Each of these vendors is bound by its own privacy and data-protection commitments.</p>

    <h2>5. Data Retention</h2>
    <p>
      We retain booking, payment, and communication records for as long as
      needed to service your rental and meet tax, accounting, and legal
      obligations — typically seven (7) years for financial records. Marketing
      and SMS opt-in preferences are retained until you opt out. You may request
      earlier deletion of non-financial records by writing to us.
    </p>

    <h2>6. Your Choices &amp; Rights</h2>
    <ul>
      <li><strong>Access &amp; correction</strong> — email us to see or correct the personal data we hold about you.</li>
      <li><strong>Deletion</strong> — email us to request deletion of non-essential personal data.</li>
      <li><strong>Email opt-out</strong> — click "unsubscribe" at the bottom of any marketing email, or email us. Transactional emails (booking confirmations, receipts) continue as long as you have an active rental.</li>
      <li><strong>SMS/WhatsApp opt-out</strong> — reply STOP as described in Section 3.</li>
      <li><strong>California, Colorado, Virginia, Connecticut, Utah &amp; other state residents</strong> — you may have additional rights, including the right to know, delete, correct, and appeal denials. Contact us to exercise those rights and we will respond within the timelines your state law requires.</li>
    </ul>

    <h2>7. Children's Privacy</h2>
    <p>
      Our services are marketed to parents and guardians renting gear for
      children in their care. We do not knowingly collect personal information
      directly from children under 13. If you believe a child has provided us
      personal information, contact us immediately and we will delete it.
    </p>

    <h2>8. Security</h2>
    <p>
      We use TLS in transit, encryption at rest for stored files, and
      vendor-managed vaults (Stripe, Twilio, Resend) for the most sensitive
      credentials. No system is perfectly secure, but we work continuously to
      keep your data safe.
    </p>

    <h2>9. Changes to this Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. Material changes
      will be posted here with a new "Last updated" date. Continued use of our
      services after the update means you accept the revised policy.
    </p>

    <h2>10. Contact Us</h2>
    <p>
      Enchanted Gear Rentals LLC<br />
      Orlando, Florida, United States<br />
      Email: <a href="mailto:info@enchantedgearrentals.com">info@enchantedgearrentals.com</a><br />
      Customer service (call or text): <a href={CS_TEL_HREF}>{CS_PHONE_DISPLAY}</a><br />
      Automated order updates (texts only): {AUTO_PHONE_DISPLAY}
    </p>
  </LegalLayout>
);

export default PrivacyPolicy;
